(() => {
  'use strict';
  function create(env, input) {
    const valid = !!input && input.schema === 1 && Array.isArray(input.routes) && Array.isArray(input.products);
    const contract = valid ? input : {};
    const origin = new URL(env.location.href).origin;
    const routes = new Map(), reverse = new Map(), products = new Map(), productPaths = new Map();
    let started = false, bootstrapped = false, publication = null, ready = Promise.resolve();
    const pathKey = value => value === '/' ? '/' : value.replace(/\/+$/, '');
    const nativeId = value => String(value ?? '').split('/').pop().replace(/^shopify-/, '');
    const routeKey = value => String(value || '').replace(/^#\/review(?=\?|$)/, '#/your-ring');
    function safeURL(value) {
      if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u001f]/.test(value)) return null;
      try {const url = new URL(value, origin);return url.origin === origin && !url.username && !url.password ? url : null;} catch {return null;}
    }
    const root = safeURL(contract.rootUrl || '/') || new URL('/', origin);
    root.search = '';root.hash = '';
    const current = safeURL(contract.current?.url || '');
    const requestedPreview = new URL(env.location.href).searchParams.get('preview_theme_id');
    const preview = /^\d+$/.test(String(contract.previewThemeId || '')) ? String(contract.previewThemeId) : /^\d+$/.test(requestedPreview || '') ? requestedPreview : '';
    function cleanQuery(value) {
      const query = new URLSearchParams(value || '');query.delete('preview_theme_id');return query;
    }
    function internalKey(value) {
      const key = routeKey(value);
      return key === '#/' ? key : /^#\/[a-z0-9_~-]+(?:\/[a-z0-9_~-]+)*\/?$/i.test(key) ? key.replace(/\/$/, '') : null;
    }
    for (const entry of contract.routes || []) {
      const key = internalKey(entry?.route), url = safeURL(entry?.url);
      if (!key || !url || url.search || url.hash || routes.has(key) || reverse.has(pathKey(url.pathname))) continue;
      routes.set(key, url.pathname);reverse.set(pathKey(url.pathname), key);
    }
    if (current && contract.current?.route) {
      const key = internalKey(contract.current.route);
      if (key && !reverse.has(pathKey(current.pathname))) reverse.set(pathKey(current.pathname), key);
    }
    function registerProduct(entry, published = false) {
      const id = nativeId(entry?.id), handle = String(entry?.handle || '');
      if (!/^[1-9]\d*$/.test(id) || !/^[a-z0-9][a-z0-9-]*$/i.test(handle) || entry?.isDemo || entry?.status && String(entry.status).toLowerCase() !== 'active') return;
      let target = entry?.url || entry?.onlineStoreUrl || (published ? root.pathname.replace(/\/?$/, '/') + 'products/' + handle : '');
      if (/^https?:\/\//i.test(target)) {
        try {const absolute = new URL(target);target = absolute.origin === origin ? absolute.pathname + absolute.search + absolute.hash : '';} catch {return;}
      }
      const url = safeURL(target);
      if (!url || url.search || url.hash || !url.pathname.endsWith('/products/' + handle)) return;
      const rawVariants = entry?.variants;
      const variants = Array.isArray(rawVariants) ? rawVariants : Array.isArray(rawVariants?.nodes) ? rawVariants.nodes : Array.isArray(rawVariants?.edges) ? rawVariants.edges.map(edge => edge.node) : [];
      const item = {id, handle, pathname:url.pathname, variants:variants.map(variant => ({id:nativeId(variant?.id || variant?.shopifyVariantId),sku:String(variant?.sku || '')}))};
      products.set(id, item);products.set('shopify-' + id, item);products.set(handle, item);productPaths.set(pathKey(url.pathname), item);
    }
    for (const entry of contract.products || []) registerProduct(entry);
    function refreshProducts() {
      const state = env.TJSShopifyCatalog?.state;
      if (state?.phase !== 'ready' || !state.products || typeof state.products.values !== 'function' || state.products === publication) return;
      publication = state.products;products.clear();productPaths.clear();
      for (const entry of contract.products || []) registerProduct(entry);
      for (const entry of publication.values()) registerProduct(entry, true);
    }
    function resolve(key) {
      refreshProducts();
      if (products.has(String(key))) return products.get(String(key));
      try {
        const product = typeof env.getProduct === 'function' ? env.getProduct(key) : typeof getProduct === 'function' ? getProduct(key) : null;
        return product && (products.get(nativeId(product.shopifyId)) || products.get(String(product.handle))) || null;
      } catch {return null;}
    }
    function productKey(item) {
      try {
        const found = typeof env.findProduct === 'function' ? env.findProduct(item) : typeof TJSNativeProduct !== 'undefined' ? TJSNativeProduct.findCatalog(product => nativeId(product.shopifyId) === item.id || product.handle === item.handle) : null;
        if (found?.id) return String(found.id);
      } catch {}
      return 'shopify-' + item.id;
    }
    function variantId(product, value) {
      const variants = product.variants.filter(row => /^[1-9]\d*$/.test(row.id));
      if (variants.some(row => row.id === value)) return value;
      const matches = variants.filter(row => row.sku === value);
      if (matches.length === 1) return matches[0].id;
      return variants.length === 0 && /^[1-9]\d*$/.test(value) ? value : null;
    }
    function decorate(url) {
      const value = new URL(url, origin);
      if (preview) value.searchParams.set('preview_theme_id', preview);
      return value.pathname + value.search + value.hash;
    }
    function internalRoute(value) {
      if (!valid) return '';
      refreshProducts();
      let url;try {url = new URL(value?.href || value || env.location.href, origin);} catch {return '';}
      if (url.origin !== origin) return '';
      try {decodeURI(url.pathname);} catch {return '#/not-found';}
      if (pathKey(url.pathname) === pathKey(root.pathname) && url.hash.startsWith('#/')) return routeKey(url.hash);
      const mapped = env.TJSNativeRouteAdapter?.internalRoute(url);
      if (mapped) return mapped;
      let key = reverse.get(pathKey(url.pathname));
      const product = productPaths.get(pathKey(url.pathname));
      if (product) key = '#/product/' + productKey(product);
      if (!key) return '';
      const query = cleanQuery(url.search);return key + (query.size ? '?' + query : '');
    }
    function nativeUrl(value) {
      if (!valid || !String(value || '').startsWith('#/')) return '';
      const route = routeKey(value), at = route.indexOf('?'), key = internalKey(at < 0 ? route : route.slice(0, at));
      if (!key) return '';
      const query = cleanQuery(at < 0 ? '' : route.slice(at + 1));
      const mapped = env.TJSNativeRouteAdapter?.nativeUrl(key + (query.size ? '?' + query : ''));
      if (mapped && safeURL(mapped)) return decorate(mapped);
      let pathname = routes.get(key);
      if (!pathname && key.startsWith('#/product/')) {
        const product = resolve(decodeURIComponent(key.slice(10))), variant = query.get('variant');
        if (product) {
          if (variant) {
            const selected = variantId(product, variant);
            if (selected) query.set('variant',selected); else query.delete('variant');
            pathname = product.pathname;
          } else pathname = product.pathname;
        }
      }
      if (!pathname && /^#\/policy\/(returns|privacy|terms|shipping)$/.test(key)) {
        try {pathname = typeof tjsNativePolicyUrl === 'function' ? tjsNativePolicyUrl(key) : '';} catch {}
      }
      if (pathname) return decorate(pathname + (query.size ? '?' + query : ''));
      const url = new URL(root.href);url.hash = key + (query.size ? '?' + query : '');return decorate(url);
    }
    function href(value) {
      const input = String(value || '');
      if (input.startsWith('#/')) return nativeUrl(input) || input;
      if (!input || input.startsWith('#') || /^(?:mailto|tel|javascript|data):/i.test(input)) return input;
      let url;try {url = new URL(input, env.location.href);} catch {return input;}
      if (url.origin === origin) {
        refreshProducts();
        const product = productPaths.get(pathKey(url.pathname)), variant = url.searchParams.get('variant');
        if (product && variant) {
          const target = nativeUrl('#/product/' + productKey(product) + '?' + cleanQuery(url.search));
          return target + (target.includes('#') ? '' : url.hash);
        }
        const prefix = root.pathname.replace(/\/?$/, '/') + 'collection/';
        if (url.pathname.startsWith(prefix)) {
          const key = '#/collection/' + url.pathname.slice(prefix.length).replace(/\/$/,'');
          if (routes.has(key)) return nativeUrl(key + (url.search ? '?' + cleanQuery(url.search) : '')) + url.hash;
        }
      }
      if (!preview) return input;
      if (url.origin !== origin || !/^(?:\/(?:[a-z]{2}(?:-[a-z]{2})?)?)?\/(?:products\/[^/]+\/?|collections\/[^/]+\/?|pages\/[^/]+\/?|blogs(?:\/[^/]+){1,2}\/?|policies\/[^/]+\/?|cart\/?|search\/?)$/.test(url.pathname) && pathKey(url.pathname) !== pathKey(root.pathname)) return input;
      return decorate(url);
    }
    const escape = value => String(value).replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
    function markup(value) {
      if (!valid) return value;
      return String(value).replace(/(<a\b[^>]*?\bhref\s*=\s*)(["'])([^"']*)\2/gi, (whole, start, quote, target) => {
        const decoded = target.replace(/&amp;/gi, '&').replace(/&#39;/g, "'").replace(/&quot;/gi, '"');
        const next = href(decoded);return next === decoded ? whole : start + quote + escape(next) + quote;
      });
    }
    function serverOnly() {
      return current && pathKey(new URL(env.location.href).pathname) === pathKey(current.pathname) && contract.current?.enhance === false && !env.TJSNativeRouteAdapter?.internalRoute(current);
    }
    function hold(phase) {
      const main = env.document.getElementById('main');
      if (!main?.hasAttribute('data-tjs-native-html')) return false;
      const unchangedNativePage = current && pathKey(new URL(env.location.href).pathname) === pathKey(current.pathname);
      const held = !valid || serverOnly() || started && !bootstrapped || phase !== 'ready';
      main.setAttribute('data-tjs-enhancement-state', !valid ? 'invalid-contract' : serverOnly() ? 'server' : held ? 'waiting' : 'ready');
      return held;
    }
    function navigateFromServerPage(target) {
      if (!valid || !env.document.getElementById('main')?.hasAttribute('data-tjs-native-html')) return false;
      const isServerOnly = serverOnly();
      const loading = env.TJSShopifyCatalog?.state && env.TJSShopifyCatalog.state.phase !== 'ready';
      if (!isServerOnly && !loading) return false;
      const url = safeURL(target);
      if (!url) return false;
      env.location.assign(url.pathname + url.search + url.hash);
      return true;
    }
    function rewrite(node) {
      if (!valid || !node) return;
      const anchors = [...(node.matches?.('a[href]') ? [node] : []), ...node.querySelectorAll?.('a[href]') || []];
      for (const anchor of anchors) {const before = anchor.getAttribute('href'), after = href(before);if (after !== before) anchor.setAttribute('href', after);}
    }
    function start() {
      if (started) return;
      started = true;
      const catalog = env.TJSShopifyCatalog;
      let initial = null;
      try {initial = JSON.parse(env.document.getElementById('tjs-shopify-public-catalog')?.textContent || 'null');} catch {}
      if (valid && initial?.schema === 1 && Number.isInteger(initial.page) && initial.page > 1 && catalog?.ready && typeof catalog.refresh === 'function') {
        ready = Promise.resolve(catalog.ready).then(() => {
          if (catalog.state?.phase === 'error' && catalog.state.error === 'Unexpected Shopify catalog page') return catalog.refresh();
        }).catch(() => {});
      }
      rewrite(env.document);
      if (typeof env.MutationObserver === 'function' && env.document.body) {
        const observer = new env.MutationObserver(records => {for (const record of records) for (const node of record.addedNodes || []) if (node.nodeType === 1) rewrite(node);});
        observer.observe(env.document.body, {childList:true, subtree:true});
      }
      const activate = () => {
        bootstrapped = true;
        rewrite(env.document);
        if (!env.document.getElementById('main')?.hasAttribute('data-tjs-native-html')) return;
        if (typeof env.route === 'function') env.route();
      };
      if (env.document.readyState === 'complete') activate();
      else env.document.addEventListener('DOMContentLoaded', activate, {once:true});
    }
    return Object.freeze({valid, get ready() {return ready;}, internalRoute, nativeUrl, href, markup, hold, navigateFromServerPage, rewrite, start});
  }
  if (typeof module !== 'undefined' && module.exports) {module.exports = {create};return;}
  let contract = null;
  try {contract = JSON.parse(document.getElementById('tjs-native-html-contract')?.textContent || 'null');} catch {}
  window.TJSNativeHtml = create(window, contract);
  window.TJSNativeHtml.start();
})();
